import { LockIcon } from "@chakra-ui/icons";
import {
  Button,
  Card,
  CardBody,
  Divider,
  HStack,
  Image,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tag,
  Text,
  UnorderedList,
  useColorModeValue,
  useMediaQuery,
  useNumberInput,
  useTheme,
} from "@chakra-ui/react";
import { safeHTML } from "@lib/html";
import { NFTEventTier } from "@models/event";
import { DEFAULT_LANGUAGE, translate } from "@models/language";
import {
  DEFAULT_MAX_PER_TRANSACTION,
  Transaction,
  TransactionItem,
  TransactionParam,
} from "@models/payment";
import { formatDate } from "@models/utils";
import { SignInModal, useUserStore } from "@providers/auth";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  formatPrice,
  usePaymentStore,
  validateNFTConstraint,
  validatePreviousTransactions,
  validateTokenConstraint,
} from "../../../payment";

const _t = (key?: string) => key || "";

const CheckoutOrderItem = ({
  index,
  tier,
}: {
  index: number;
  tier: NFTEventTier;
  disabled?: boolean;
}) => {
  const transaction = usePaymentStore((state) => state.transaction);
  const event = usePaymentStore((state) => state.event);
  const user = useUserStore((state) => state.user);
  const language = DEFAULT_LANGUAGE;
  const [initialized, setInitialized] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [soldOut, setSoldOut] = useState(false);
  const [salesEnded, setSalesEnded] = useState(false);
  const [salesNotStarted, setSalesNotStarted] = useState(false);
  const [nftHolderOnly, setNFTHolderOnly] = useState(false);
  const [tokenHolderOnly, setTokenHolderOnly] = useState(false);
  const [whitelistOnly, setWhitelistOnly] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [maxQuantity, setMaxQuantity] = useState(
    tier.paymentConfig?.maxPerTransaction ?? DEFAULT_MAX_PER_TRANSACTION
  );
  const [isMobile] = useMediaQuery("(max-width: 640px)");
  const [showSignInModal, setShowSignInModal] = useState(false);
  const textColor = useColorModeValue("", "neutral.10");

  const info = useMemo(() => {
    if (!tier) return {};
    return translate(tier.info, language);
  }, [tier, language]);

  const salesStart = useMemo(() => {
    let start = Date.now();
    if (event && tier?.paymentConfig) {
      start = tier.paymentConfig.start || start;
    }
    return formatDate(start, event?.timezone, "MMM dd");
  }, [event, tier]);

  const currentPriceDiscountPercentage = useMemo(
    () => tier?.paymentConfig?.currentPriceDiscountPercentage || 0,
    [tier]
  );

  const { data: nftQuantity } = useQuery(
    ["validateNFTConstraint", tier.id],
    () => validateNFTConstraint(user!.id, tier.paymentConfig!.nftConstraint!),
    {
      enabled: nftHolderOnly && !!user && !!tier.paymentConfig!.nftConstraint,
    }
  );

  const { data: tokenQuantity } = useQuery(
    ["validateTokenConstraint", tier.id],
    () =>
      validateTokenConstraint(user!.id, tier.paymentConfig!.tokenConstraint!),
    {
      enabled:
        tokenHolderOnly && !!user && !!tier.paymentConfig!.tokenConstraint,
    }
  );

  const { data: previousTransactionQuantity } = useQuery(
    ["validatePreviousTransactions", tier.id],
    () => validatePreviousTransactions(event!.id, tier.id),
    {
      enabled: !!event && !!tier && !!user,
    }
  );

  const isNFTHolder: boolean = useMemo(() => {
    if (!nftQuantity) return false;
    return nftQuantity > 0;
  }, [nftQuantity]);

  const isTokenHolder: boolean = useMemo(() => {
    if (!tokenQuantity) return false;
    return tokenQuantity > 0;
  }, [tokenQuantity]);

  const selected = useMemo(() => {
    if (isEmpty(transaction?.items)) return false;
    return !!transaction?.items.find((item) => item.productId === tier.id);
  }, [transaction, tier]);

  const availableQuantity = useMemo(() => {
    if (!tier.paymentConfig) return 0;

    const discountRemainUse =
      transaction?.discount && transaction?.discount.max > 0
        ? transaction.discount.remainUse
        : -1;

    const remainQuantity =
      tier.paymentConfig.max < 0
        ? tier.paymentConfig.maxPerTransaction ?? DEFAULT_MAX_PER_TRANSACTION
        : Math.max(tier.paymentConfig.max - tier.paymentConfig.sold, 0);

    return discountRemainUse > 0
      ? Math.min(discountRemainUse, remainQuantity)
      : remainQuantity;
  }, [tier?.paymentConfig, transaction?.discount?.id]);

  const maxSelectQuantity = useMemo(() => {
    return Math.min(
      tier?.paymentConfig?.disableBuyForOthers
        ? 1
        : tier?.paymentConfig?.maxPerTransaction ?? DEFAULT_MAX_PER_TRANSACTION,
      maxQuantity,
      availableQuantity
    );
  }, [tier?.paymentConfig, maxQuantity, availableQuantity]);

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: disabled ? 0 : 1,
      defaultValue: disabled ? 0 : quantity,
      min: 0,
      max: maxSelectQuantity,
      precision: 0,
      onChange: (str, value) => setQuantity(value),
      value: quantity,
    });
  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  useEffect(() => {
    if (!initialized) return;
    const _transaction = usePaymentStore.getState().transaction;
    if (!event || !_transaction || !tier.paymentConfig) return;
    let currentItems =
      _transaction.items.filter((i) => i.productId == tier.id) || [];
    const otherItems =
      _transaction.items.filter((i) => i.productId !== tier.id) || [];
    const currenPrice = tier.paymentConfig.currentPrice;
    const diff = Math.min(quantity, maxSelectQuantity) - currentItems.length;
    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        currentItems.push(
          new TransactionItem({
            productId: tier.id,
            name: info.name || tier.name || tier.id,
            price: currenPrice.price,
            priceId: currenPrice.id,
          })
        );
      }
    } else if (diff < 0) {
      currentItems = currentItems.slice(0, diff);
    }

    _transaction.items = [...otherItems, ...currentItems].sort(
      (a, b) => a.createdAt - b.createdAt
    );

    // Remove previous auto discount if any
    if (_transaction.discount?.auto) _transaction.removeDiscount();
    // Apply auto discount if any
    const autoDiscount = _transaction.getAutoDiscount(tier.paymentConfig);
    console.log("auto discount", autoDiscount);

    let discount = _transaction.discount;

    if (autoDiscount) {
      if (_transaction.discount && !_transaction.discount.auto) {
        const originalAmount = _transaction.originalAmount;
        if (
          autoDiscount.calculateDiscount(originalAmount) >
          _transaction.discount.calculateDiscount(originalAmount)
        ) {
          discount = autoDiscount;
        }
      } else {
        discount = autoDiscount;
      }
    }
    if (discount) _transaction.applyDiscount(discount);

    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
      ...(discount && !isEmpty(discount) ? { discount } : { discount: null }),
    });
  }, [quantity, maxSelectQuantity, initialized, tier, event]);

  useEffect(() => {
    if (!transaction || !tier?.paymentConfig || !event) return;

    let _disabled = false;

    console.log(
      `${tier.name} sold: ${tier.paymentConfig.sold}/${tier.paymentConfig.max}`
    );

    const now = Date.now();
    const _salesEnded =
      now > tier.paymentConfig.end || now > event.end + 86400000;
    setSalesEnded(_salesEnded);

    const _salesNotStarted = now < tier.paymentConfig.start;
    setSalesNotStarted(_salesNotStarted);

    const _soldOut =
      tier.paymentConfig.max > 0
        ? tier.paymentConfig.sold >= tier.paymentConfig.max
        : false;
    setSoldOut(_soldOut);

    const _nftHolderOnly = !isEmpty(
      tier.paymentConfig.nftConstraint?.collections
    );
    setNFTHolderOnly(_nftHolderOnly);

    const _tokenHolderOnly = !isEmpty(
      tier.paymentConfig.tokenConstraint?.contracts
    );
    setTokenHolderOnly(_tokenHolderOnly);

    const _whitelistOnly = !isEmpty(tier.paymentConfig.whitelist);
    setWhitelistOnly(_whitelistOnly);

    const _limitedTransactionQuantity = !!(
      tier.paymentConfig.maxPerUser && tier.paymentConfig.maxPerUser > 0
    );

    const overallTransactionLimit =
      tier.paymentConfig.maxPerUser && tier.paymentConfig.maxPerUser > 0
        ? Math.max(
            tier.paymentConfig.maxPerUser - (previousTransactionQuantity || 0),
            0
          )
        : -1;

    console.log(tier.name, {
      previousTransactionQuantity,
      max: tier.paymentConfig.maxPerUser,
      overallTransactionLimit,
    });

    if (_salesNotStarted || _salesEnded || _soldOut) {
      _disabled = true;
    }

    if (transaction.discount) {
      console.log("discount remains:", transaction.discount.remainUse);
      if (!event.allowCrossTierPurchase) {
        if (transaction.discount.productId) {
          if (transaction.discount.productId != tier.id) _disabled = true;
        } else {
          if (
            transaction.items.length > 0 &&
            transaction.items[0].productId != tier.id
          )
            _disabled = true;
        }
      }

      if (
        tier.paymentConfig.inviteOnly &&
        transaction.discount.productId != tier.id
      )
        _disabled = true;
    } else {
      if (
        !event.allowCrossTierPurchase &&
        transaction.items.length > 0 &&
        transaction.items[0].productId != tier.id
      )
        _disabled = true;

      if (tier.paymentConfig.inviteOnly) _disabled = true;
    }

    // Check account purchase limit
    if (overallTransactionLimit >= 0) {
      if (overallTransactionLimit == 0) {
        _disabled = true;
      } else {
        setMaxQuantity(overallTransactionLimit);
      }
    }

    if (_nftHolderOnly || _tokenHolderOnly) {
      if (user) {
        let nftLimit =
          nftQuantity &&
          tier.paymentConfig.nftConvertRatio &&
          tier.paymentConfig.nftConvertRatio > 0
            ? Math.floor(nftQuantity / tier.paymentConfig.nftConvertRatio)
            : -1;

        let tokenLimit =
          tokenQuantity &&
          tier.paymentConfig.tokenConvertRatio &&
          tier.paymentConfig.tokenConvertRatio > 0
            ? Math.floor(tokenQuantity / tier.paymentConfig.tokenConvertRatio)
            : -1;
        console.log(tier.name, {
          overallTransactionLimit,
          nftLimit,
          tokenLimit,
        });

        if (previousTransactionQuantity && previousTransactionQuantity > 0) {
          if (nftLimit >= 0) {
            nftLimit = Math.max(nftLimit - previousTransactionQuantity, 0);
          }
          if (tokenLimit >= 0) {
            tokenLimit = Math.max(tokenLimit - previousTransactionQuantity, 0);
          }
        }

        console.log(tier.name, {
          overallTransactionLimit,
          nftLimit,
          tokenLimit,
        });

        if (_nftHolderOnly && _tokenHolderOnly) {
          const combinedLimit = Math.min(nftLimit, tokenLimit);
          if (!isNFTHolder || !isTokenHolder) {
            _disabled = true;
          } else {
            if (combinedLimit >= 0) {
              const overallLimit =
                overallTransactionLimit >= 0
                  ? Math.min(overallTransactionLimit, combinedLimit)
                  : combinedLimit;
              console.log({
                overallTransactionLimit,
                combinedLimit,
                overallLimit,
              });
              setMaxQuantity(overallLimit);
              if (overallLimit === 0) _disabled = true;
            }
          }
        } else if (_nftHolderOnly) {
          if (!isNFTHolder) {
            _disabled = true;
          } else {
            if (nftLimit >= 0) {
              const overallLimit =
                overallTransactionLimit >= 0
                  ? Math.min(overallTransactionLimit, nftLimit)
                  : nftLimit;
              console.log({ overallTransactionLimit, nftLimit, overallLimit });
              setMaxQuantity(overallLimit);
              if (overallLimit === 0) _disabled = true;
            }
          }
        } else if (_tokenHolderOnly) {
          if (!isTokenHolder) {
            _disabled = true;
          } else {
            if (tokenLimit >= 0) {
              const overallLimit =
                overallTransactionLimit >= 0
                  ? Math.min(overallTransactionLimit, tokenLimit)
                  : tokenLimit;
              console.log({
                overallTransactionLimit,
                tokenLimit,
                overallLimit,
              });
              setMaxQuantity(overallLimit);
              if (overallLimit === 0) _disabled = true;
            }
          }
        }
      } else {
        _disabled = true;
      }
    }

    if (_whitelistOnly) {
      if (
        user &&
        tier.paymentConfig.whitelist &&
        !isEmpty(tier.paymentConfig.whitelist)
      ) {
        if (
          !user.identifiers.some((i) => tier.paymentConfig!.checkWhitelist(i))
        )
          _disabled = true;
      } else {
        _disabled = true;
      }
    }

    setDisabled(_disabled);

    if (!_disabled) {
      const _quantity = transaction.items.filter(
        (i) => i.productId == tier.id
      ).length;
      if (_quantity > 0) setQuantity(_quantity);
    } else {
      setQuantity(0);
    }
    if (!initialized) setInitialized(true);
  }, [
    tier?.paymentConfig,
    transaction,
    user,
    event,
    isNFTHolder,
    isTokenHolder,
    nftQuantity,
    tokenQuantity,
    previousTransactionQuantity,
    initialized,
  ]);

  useEffect(() => {
    if (disabled) setQuantity(0);
  }, [disabled]);

  if (!tier.paymentConfig || !transaction) return <></>;

  const selectTier = () => {
    const _transaction = usePaymentStore.getState().transaction;
    if (!event || !_transaction || !tier.paymentConfig) return;
    const currenPrice = tier.paymentConfig.currentPrice;
    _transaction.items = [
      new TransactionItem({
        productId: tier.id,
        name: info.name || tier.name || tier.id,
        price: currenPrice.price,
        priceId: currenPrice.id,
      }),
    ];

    // set transaction subscription if any
    _transaction.subscription = !isEmpty(
      tier.paymentConfig.stripeConfig?.subscription
    );

    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  const removeTier = () => {
    const _transaction = usePaymentStore.getState().transaction;
    if (!event || !_transaction || !tier.paymentConfig) return;
    _transaction.items = [];
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  const theme = useTheme();

  return (
    <>
      <Card
        direction={{ base: "column", lg: "row" }}
        key={index}
        className={
          disabled
            ? "cursor-not-allowed"
            : "focus:border-primary-300 hover:border-primary-300"
        }
        bgColor={"transparent"}
      >
        <CardBody p="0">
          {(nftHolderOnly || tokenHolderOnly || whitelistOnly) && !user && (
            <div className="w-full h-full bg-transparent absolute top-0 bottom-0 left-0 right-0 z-10 flex flex-row items-center justify-center text-white">
              <Button
                leftIcon={<LockIcon color={"white"} />}
                onClick={() => setShowSignInModal(true)}
                color={useColorModeValue("", "white")}
              >
                {_t("Login to Unlock")}
              </Button>
            </div>
          )}
          <div
            className={
              "flex flex-row dark:bg-white/[.05] bg-white rounded-md overflow-hidden " +
              (disabled ? "opacity-50" : "")
            }
          >
            {tier.imageUrl && (
              <div className="h-fit flex-shrink-0">
                <Image
                  className="w-[150px] max-h-full hidden lg:block "
                  objectFit="contain"
                  src={tier.imageUrl}
                  alt={info.name}
                  // fallbackSrc="https://via.placeholder.com/150"
                />
              </div>
            )}
            <div className="flex flex-grow gap-3 flex-col text-neutral-30 dark:bg-transparent bg-white relative rounded-md px-9 py-4 ">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center justify-start flex-wrap width:100% gap-2">
                  <div>
                    <Text
                      fontWeight="medium"
                      fontSize="xl"
                      mr={"1rem"}
                      lineHeight="1.5rem"
                      color={textColor}
                    >
                      {info.name || ""}
                    </Text>
                  </div>

                  {tier.paymentConfig.inviteOnly && (
                    <Tag
                      bgColor="tags.inviteOnly.bg"
                      textColor="tags.inviteOnly.text"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {_t("Invite Only")}
                    </Tag>
                  )}

                  {!!currentPriceDiscountPercentage && (
                    <Tag
                      bgColor="tags.discount.bg"
                      textColor="tags.discount.text"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {`${currentPriceDiscountPercentage.toFixed(0)} % OFF`}
                    </Tag>
                  )}

                  {soldOut && (
                    <Tag
                      bgColor="error"
                      textColor="white"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {_t("Sold Out")}
                    </Tag>
                  )}

                  {salesEnded && (
                    <Tag
                      bgColor="error"
                      textColor="white"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {_t("Sales Ended")}
                    </Tag>
                  )}

                  {salesNotStarted && (
                    <Tag
                      bgColor="error"
                      textColor="white"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {_t(`Sales start on ${salesStart}`)}
                    </Tag>
                  )}

                  {(nftHolderOnly || tokenHolderOnly) && (
                    <Tag
                      bgColor="tags.holderOnly.bg"
                      textColor="tags.holderOnly.text"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {_t("Holder Only")}
                    </Tag>
                  )}

                  {whitelistOnly && (
                    <Tag
                      bgColor="tags.whitelistOnly.bg"
                      textColor="tags.whitelistOnly.text"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      textTransform="capitalize"
                    >
                      {_t("Whitelist Only")}
                    </Tag>
                  )}
                </div>

                {event?.isERC1155BulkPurchase ? (
                  selected ? (
                    <Button
                      isDisabled={disabled}
                      variant="gradient"
                      onClick={removeTier}
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      isDisabled={disabled}
                      variant="gradient"
                      onClick={selectTier}
                    >
                      Select
                    </Button>
                  )
                ) : (
                  <HStack>
                    <Button
                      {...dec}
                      isDisabled={disabled}
                      variant="symbol"
                      size="symbol"
                      color={"white"}
                    >
                      -
                    </Button>
                    <Text
                      minW="2rem"
                      textAlign="center"
                      textColor={+input.value == 0 ? "neutral.600" : textColor}
                      fontWeight="bold"
                    >
                      {input.value}
                    </Text>
                    <Button
                      {...inc}
                      isDisabled={disabled}
                      variant="symbol"
                      size="symbol"
                      color={"white"}
                    >
                      +
                    </Button>
                  </HStack>
                )}
              </div>
              <Divider />
              <div className="flex flex-col">
                <p className="text-lg text-neutral-100">
                  {!!currentPriceDiscountPercentage && (
                    <span className="no-underline text-primary font-medium">
                      {tier.paymentConfig.currentPrice.price > 0
                        ? formatPrice(
                            tier.paymentConfig.currentPrice.price,
                            transaction.currency
                          )
                        : _t("Free")}
                      &nbsp; &nbsp;
                    </span>
                  )}
                  <span
                    className={
                      "font-medium " +
                      (currentPriceDiscountPercentage
                        ? "line-through"
                        : "text-primary")
                    }
                  >
                    {tier.paymentConfig.defaultPrice.price > 0
                      ? formatPrice(
                          tier.paymentConfig.defaultPrice.price,
                          transaction.currency
                        )
                      : _t("Free")}
                  </span>
                </p>

                {/* subscription */}
                <span className="text-xs">
                  {tier.paymentConfig?.subscriptionInterval}
                </span>
              </div>
              {info && info.description && (
                <div className="html-container">
                  <p
                    className="dark:text-neutral-100 whitespace-pre-wrap "
                    dangerouslySetInnerHTML={safeHTML(info.description)}
                  ></p>
                </div>
              )}
              {info && info.rewards && (
                <UnorderedList>
                  {info.rewards?.map((reward, i) => (
                    <ListItem key={i} className="dark:text-neutral-100">
                      {reward}
                    </ListItem>
                  ))}
                </UnorderedList>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {(nftHolderOnly || whitelistOnly || tokenHolderOnly) && !user && (
        <Modal
          isOpen={showSignInModal}
          onClose={() => setShowSignInModal(false)}
          isCentered
          size={isMobile ? "full" : "md"}
          trapFocus={false}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <SignInModal onAuthenticated={() => setShowSignInModal(false)} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default CheckoutOrderItem;
